import { defineComponent as _defineComponent } from 'vue';
import { createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, createBlock as _createBlock, createTextVNode as _createTextVNode, Fragment as _Fragment, withCtx as _withCtx, unref as _unref, createSlots as _createSlots } from "vue";
const _hoisted_1 = { class: "td-v2" };
const _hoisted_2 = {
    key: 0,
    class: "merchant-hero"
};
const _hoisted_3 = {
    key: 1,
    class: "merchant-hero"
};
const _hoisted_4 = ["src"];
const _hoisted_5 = { class: "merchant-name" };
const _hoisted_6 = {
    key: 3,
    class: "category-block"
};
const _hoisted_7 = ["src"];
const _hoisted_8 = ["href"];
const _hoisted_9 = { class: "switch-top" };
const _hoisted_10 = { class: "switch-top-left" };
const _hoisted_11 = { class: "switch-avatars" };
const _hoisted_12 = ["src"];
const _hoisted_13 = ["src"];
const _hoisted_14 = {
    key: 11,
    class: "summary-block"
};
const _hoisted_15 = { class: "summary-heading" };
const _hoisted_16 = {
    key: 12,
    class: "tagSection"
};
const _hoisted_17 = { class: "drawer-tx-wrap" };
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAppState } from '@/composables/useAppState';
import { useAuth } from '@/composables/useAuth';
import { useTransactions } from '@/composables/useTransactions';
import { usePolling } from '@/composables/usePolling';
import { getSpendingAnalysis, formatTimestampString, findCurrentMonthIndex, groupTransactionsByDay } from '@/composables/useSpendingAnalysis';
import HeaderBlock from './elements/HeaderBlock.vue';
import CategoryLozenge from './elements/CategoryLozenge.vue';
import SpendingSummaryCard from './elements/SpendingSummaryCard.vue';
import TransactionsList from './elements/TransactionsList.vue';
import RecentTransactionsSection from './elements/RecentTransactionsSection.vue';
import AiChatBanner from './elements/AiChatBanner.vue';
import SimpleDrawerPanel from './elements/SimpleDrawerPanel.vue';
import ChangeCategoryDrawer from './elements/ChangeCategoryDrawer.vue';
import BudgetSummaryCard from './elements/BudgetSummaryCard.vue';
import TagInput from './elements/TagInput.vue';
import WellPanel from './elements/WellPanel.vue';
import RetailDiscountCard from './elements/RetailDiscountCard.vue';
import { useCustomer } from '@/composables/useCustomer';
import { useBanking, pendingBankingRevalidate } from '@/composables/useBanking';
import { useVouchers } from '@/composables/useVouchers';
import { selectedFilter, selectedSubCategory, selectedSubCategoryColour, selectedMerchant, cameFromTransactionDetail, } from './elements/spendingAnalysisFilterState';
import { pendingTransactionUpdateId, pendingWriteRevalidate, consumePendingWriteRevalidate, inflightCategoryUpdate, txnFieldsKey } from './elements/transactionUpdateState';
import { getMerchantLogoUrl, getMerchantLogoStyle, onMerchantLogoError, isWellKnownLogosOnly } from '@/utils/helpers';
// ---------- Pure helpers ----------
// [startOfMonth 5 months ago, now] — gives 6 monthly buckets inclusive of the current month.
export default /*@__PURE__*/ _defineComponent({
    __name: 'TransactionDetailPage',
    setup(__props) {
        function sixMonthRange() {
            const now = new Date();
            const fromDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1));
            // First instant of next month — keeps today's transactions inside the
            // window regardless of timezone offset.
            const toDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
            return { fromDate, toDate };
        }
        // Map the compact spending-analysis transaction shape to the richer shape
        // TransactionsList expects (effectiveClassification object, merchant object,
        // timestampString for the date-separator header).
        function reshapeSpendingTx(apiTx) {
            return {
                id: apiTx.id,
                merchantName: apiTx.merchantName,
                merchant: apiTx.wellKnownMerchant ? { name: apiTx.merchantName } : null,
                wellKnownMerchant: apiTx.wellKnownMerchant,
                amount: apiTx.amount,
                timestamp: apiTx.timestamp,
                timestampString: formatTimestampString(apiTx.timestamp),
                customerTags: apiTx.tags ?? apiTx.customerTags,
                effectiveClassification: {
                    mgClassification: apiTx.classification,
                    mgSubClassification: apiTx.subClassification,
                    color: apiTx.color,
                    iconPath: apiTx.icon,
                },
            };
        }
        // ---------- Composables ----------
        const route = useRoute();
        const router = useRouter();
        const state = useAppState();
        useAuth();
        const transactionsApi = useTransactions();
        const { getCustomerTags, clearCustomerTagsCache } = useCustomer();
        const { getBankingInfo, applyExcludeOptimistically } = useBanking();
        const { getRecommendedVouchers, getAvailableVouchers } = useVouchers();
        // Distinct key from the other usePolling instances (banking / activity / etc.)
        // — both poll-call sites in this file share one timeout slot, so re-using
        // 'banking' would let unrelated banking polls cancel each other.
        const { poll } = usePolling('tdp-txn-poll');
        const wellKnownLogosOnlyGlobal = getCurrentInstance()?.appContext.config.globalProperties.$wellKnownLogosOnly;
        // ---------- Template refs ----------
        const categoryDrawer = useTemplateRef('categoryDrawer');
        const transactionsDrawerOpen = ref(false);
        // Set by onCategoryApply right before the upfront fetchSummary call so that
        // the subsequent classification-key watcher firing (caused by pollForChange
        // reassigning transaction.value) doesn't trigger a redundant second fetch.
        const skipNextSummaryFetch = ref(false);
        // ---------- Reactive state ----------
        // Single source of truth: always fetched via the direct API. The server caches,
        // so this is cheap and avoids the drift/edge-cases of scanning recentTransactions.
        const transaction = ref(null);
        // Server content hash (x-mgcache-md5) of the currently-loaded transaction.
        // Used as the baseline the post-write poll compares against to detect when the
        // server payload reflects the change. Held as a ref (not re-read from cache) so
        // it survives the cache TTL if the page has been idle.
        const currentTransactionHash = ref(null);
        // Monthly spend for this transaction's sub-classification over the last 6
        // months, fetched from the spending analysis API. Populated by fetchSummary().
        const monthlyData = ref([]);
        const summaryLoading = ref(false);
        // Transactions for this merchant (within the same 12-month window as the chart),
        // grouped by date for TransactionsList. Populated by fetchSummary().
        const merchantTxGroups = ref([]);
        // `switchable` comes from the spending-analysis response (the direct
        // transaction endpoint doesn't return it), so capture it during fetchSummary.
        const switchableFromAnalysis = ref(false);
        // Budget (plan) details for this transaction's sub-category, captured during
        // fetchSummary. Populated when the matching category has a plan.
        const categoryBudget = ref(null);
        // Customer hashtags
        const selectedTags = ref([]);
        const availableTags = ref(null);
        // Matching recommended voucher for this merchant — populated via getRecommendedVouchers
        // so we can show the user's spend at this merchant and the potential saving.
        const merchantRecommendedVoucher = ref(null);
        // ---------- Computeds: identity ----------
        const transactionId = computed(() => String(route.params.id ?? ''));
        // Both derived from the shared inflightCategoryUpdate marker so the in-flight
        // UI (disabled "Working...." lozenge + history-row spinners) persists across
        // navigation away from TDP and back while the SA / txn polls finish in the
        // background. The marker is cleared by the SA pollUntilChanged response.
        const categoryUpdating = computed(() => inflightCategoryUpdate.value?.id === transactionId.value);
        // Flattened ids of all transactions for this merchant in the loaded window —
        // shared between the row-spinner computed and any other consumer that needs
        // a per-id lookup (avoids re-flattening on each computed re-evaluation).
        const merchantTxIds = computed(() => merchantTxGroups.value.flat().map((tx) => tx.id).filter((id) => !!id));
        const updatingTransactionId = computed(() => {
            const inflight = inflightCategoryUpdate.value;
            if (!inflight || inflight.id !== transactionId.value)
                return null;
            if (!inflight.allMerchant)
                return inflight.id;
            return merchantTxIds.value.length > 0 ? merchantTxIds.value : inflight.id;
        });
        const merchantName = computed(() => {
            const t = transaction.value;
            if (!t)
                return '';
            return t.merchant?.name ?? t.merchantName ?? '';
        });
        const categoryName = computed(() => {
            const t = transaction.value;
            if (!t)
                return 'Uncategorised';
            const c = t.effectiveClassification;
            return c?.mgSubClassification || c?.mgClassification || 'Uncategorised';
        });
        // Merchant trend uses a fixed purple regardless of the transaction's category.
        const barColour = computed(() => '#413655');
        // ---------- Computeds: hero display ----------
        const isIncome = computed(() => (transaction.value?.amount ?? 0) > 0);
        const amountDisplay = computed(() => {
            const amt = transaction.value?.amount ?? 0;
            const formatted = state.formatter.format(Math.abs(amt));
            return isIncome.value ? '+' + formatted : formatted;
        });
        const descriptionDisplay = computed(() => {
            const desc = transaction.value?.description ?? '';
            return desc && desc !== merchantName.value ? desc : '';
        });
        const fullDateDisplay = computed(() => {
            const ts = transaction.value?.timestamp;
            if (!ts)
                return '';
            return new Date(ts).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        });
        const accountDisplay = computed(() => {
            const id = transaction.value?.bankAccountID;
            if (!id)
                return '';
            const account = (state.bankInfo?.accounts ?? []).find((a) => a.id === id);
            if (!account)
                return '';
            return account.bank?.prettyName ?? account.bank?.name ?? account.bankDisplayName ?? '';
        });
        const moneyDirectionDisplay = computed(() => (isIncome.value ? 'Money in' : 'Money out'));
        const spendingAnalysisDisplay = computed(() => (transaction.value?.excludeFromAnalysis ? 'Excluded' : 'Included'));
        const rawDescriptionDisplay = computed(() => transaction.value?.description ?? '');
        const detailsWellData = computed(() => {
            const rows = [
                { order: 1, label: 'Date', value: fullDateDisplay.value },
                { order: 2, label: 'Account', value: accountDisplay.value },
                { order: 3, label: 'Transaction', value: moneyDirectionDisplay.value },
                { order: 4, label: 'Spending Analysis', value: spendingAnalysisDisplay.value },
            ];
            if (rawDescriptionDisplay.value) {
                rows.push({ order: 5, label: 'Description', value: rawDescriptionDisplay.value });
            }
            return rows;
        });
        // ---------- Computeds: retail-card discount ----------
        // Find an available retail-card voucher matching this transaction's merchant.
        // `state.availableVouchers` is loaded on mount. Match by sysoMerchantId first
        // (the transaction's merchant.id maps to the voucher's merchant.sysoMerchantId,
        // the same mapping attachAvailableVouchers uses), then fall back to merchant
        // name — the display name (e.g. "Currys") often differs from the voucher name
        // (e.g. "Currys B2B"), so name alone misses valid matches.
        const merchantDiscount = computed(() => {
            const t = transaction.value;
            const vouchers = state.availableVouchers ?? [];
            if (!t || vouchers.length === 0)
                return null;
            const sysoId = t.merchant?.id;
            const name = merchantName.value;
            const match = vouchers.find((v) => (sysoId && v.merchant?.sysoMerchantId === sysoId) ||
                (name && v.merchant?.name?.toLowerCase() === name.toLowerCase()));
            if (!match || !(match.effectiveDiscountPercentage > 0))
                return null;
            return match;
        });
        const discountSpendDisplay = computed(() => {
            const spent = merchantRecommendedVoucher.value?.spent;
            if (spent === undefined || spent === null)
                return '';
            return state.formatter.format(Math.abs(spent));
        });
        const discountSavingDisplay = computed(() => {
            const saving = merchantRecommendedVoucher.value?.saving;
            if (saving === undefined || saving === null)
                return '';
            return state.formatter.format(Math.abs(saving));
        });
        // ---------- Computeds: chart ----------
        const currentMonthIndex = computed(() => findCurrentMonthIndex(monthlyData.value));
        // ---------- Computeds: switch-to-save ----------
        const showSwitchToSave = computed(() => transaction.value?.switchable === true || switchableFromAnalysis.value);
        const switchToSaveHref = computed(() => {
            const person = state.customer?.person;
            const email = person?.email ?? '';
            const postCode = person?.addresses?.[0]?.postCode ?? '';
            return 'https://moneyguided.aptap.co.uk?p1=' + email + '&p2=' + postCode;
        });
        // ---------- Computeds: merchant logo ----------
        const merchantLogoInput = computed(() => {
            const t = transaction.value;
            return {
                name: merchantName.value,
                wellKnown: !!t?.merchant || t?.wellKnownMerchant === true,
                iconPath: t?.effectiveClassification?.iconPath,
                iconColour: t?.effectiveClassification?.color,
                wellKnownLogosOnly: isWellKnownLogosOnly(wellKnownLogosOnlyGlobal),
            };
        });
        const merchantLogoUrl = computed(() => {
            const t = transaction.value;
            // Prefer an API-supplied logo URL when present; otherwise use the shared fallback chain.
            if (t?.merchant?.logoUrl)
                return t.merchant.logoUrl;
            return getMerchantLogoUrl(merchantLogoInput.value);
        });
        const merchantLogoStyle = computed(() => getMerchantLogoStyle(merchantLogoInput.value));
        // A category icon (white-on-transparent PNG on a coloured circle) vs a real brand
        // logo. getMerchantLogoStyle only returns a backgroundColor for the category case,
        // so reuse that as the signal — keeps the rule in one place.
        const avatarIsCategory = computed(() => !!merchantLogoStyle.value.backgroundColor);
        // Full list of this merchant's transactions within the summary window.
        const allMerchantTransactions = computed(() => merchantTxGroups.value);
        // ---------- Fetch ----------
        function loadTransaction(id) {
            if (!id) {
                transaction.value = null;
                selectedTags.value = [];
                return;
            }
            if (transaction.value?.id === id)
                return;
            transaction.value = null;
            selectedTags.value = [];
            transactionsApi.getTransaction(id, (t) => {
                if (t?.id !== transactionId.value)
                    return;
                transaction.value = t;
                currentTransactionHash.value = transactionsApi.getCachedTransactionHash(id);
                selectedTags.value = Array.isArray(t?.customerTags)
                    ? t.customerTags.map((ct) => ct.tag)
                    : [];
                // Came back into TDP while a category update is still in flight: the
                // initial fetch can hit a stale server-side /Cached/ response, leaving
                // the lozenge showing the previous category. Re-run the same poll
                // loop against the pre-write baseline captured in the inflight marker
                // (currentTransactionHash here may already be the post-change hash,
                // since TDP1's poll updated the cache before unmount).
                const inflight = inflightCategoryUpdate.value;
                if (inflight?.id === id && inflight.baselineHash) {
                    pollTxnUntilHashDiffers(id, inflight.baselineHash, inflight.baselineFields, ({ latestFetched, hadChange }) => {
                        if (hadChange && latestFetched?.id === transactionId.value) {
                            transaction.value = latestFetched;
                            currentTransactionHash.value = transactionsApi.getCachedTransactionHash(id);
                        }
                    });
                }
            });
        }
        // Shared txn-endpoint poll. Re-fetches getTransaction at a fixed cadence until
        // the cached server hash differs from `baselineHash` (or attempts exhaust).
        // Falls back to comparing classification + exclude fields against
        // `baselineFields` when the server response carries no x-mgcache-md5 header
        // (non-/Cached/ endpoints), so the poll still detects the write either way.
        // `onDone` always fires once; `hadChange` reports whether divergence was seen.
        function pollTxnUntilHashDiffers(id, baselineHash, baselineFields, onDone) {
            let latestFetched = null;
            const hasChanged = () => {
                const h = transactionsApi.getCachedTransactionHash(id);
                if (h && baselineHash)
                    return h !== baselineHash;
                return !!latestFetched && txnFieldsKey(latestFetched) !== baselineFields;
            };
            poll({
                remaining: 15,
                interval: 1500,
                fetch: (cb) => { transactionsApi.getTransaction(id, (fresh) => { latestFetched = fresh; cb(); }); },
                onPoll: () => { },
                shouldStop: hasChanged,
                onFinish: () => { onDone({ latestFetched, hadChange: !!latestFetched && hasChanged() }); },
            });
        }
        // Fetch the last 6 months of spending filtered to this merchant. The response
        // powers both the chart (timeSlicedBreakdowns) and the merchant-transactions
        // list (categories[].spendByMerchant[].transactions).
        function fetchSummary() {
            const t = transaction.value;
            if (!t)
                return;
            const pollUntilChanged = consumePendingWriteRevalidate();
            const subName = t.effectiveClassification?.mgSubClassification;
            // Spending analysis returns the raw bank descriptor in `merchantName`, while
            // the full transaction may also expose a normalised `merchant.name`. Match
            // against either so the list isn't empty when the two forms differ.
            const merchantNames = new Set([t.merchantName, t.merchant?.name].filter((n) => !!n));
            const merchantForFetch = t.merchantName || t.merchant?.name || '';
            summaryLoading.value = true;
            const { fromDate, toDate } = sixMonthRange();
            // Always use calendar months for the merchant history — snapping to the
            // user's spending cycle can clip today's transactions out of the window.
            getSpendingAnalysis(fromDate, toDate, { merchant: merchantForFetch, snapToSpendingCycle: false }, (result, isStale) => {
                // While polling for a server change, keep the skeleton up until the
                // fresh callback lands.
                if (!(pollUntilChanged && isStale)) {
                    summaryLoading.value = false;
                }
                if (!isStale && pollUntilChanged) {
                    // Only clear the in-flight marker when this is the response from
                    // the pollUntilChanged cycle kicked off by the write — i.e. the SA
                    // payload genuinely reflects the new classifications. A normal
                    // (non-polling) fetchSummary triggered by the watcher would also
                    // fire !isStale, but possibly with the previous snapshot, which
                    // would clear the spinners a beat before the rows actually update.
                    // This single write also flips `categoryUpdating` (lozenge) and
                    // `updatingTransactionId` (row spinners) via their computeds.
                    inflightCategoryUpdate.value = null;
                }
                const windows = result?.timeSlicedBreakdowns ?? [];
                monthlyData.value = windows.map((w) => ({
                    label: w.label,
                    // Use whichever side is non-zero so income categories (where totalOut=0) still render bars.
                    value: Math.max(w.totalOut ?? 0, w.totalIn ?? 0),
                    fromDate: w.fromDate,
                    toDate: w.toDate,
                }));
                // Collect transactions for this merchant across the sub-category breakdowns.
                const flat = [];
                const cats = result?.categories ?? [];
                switchableFromAnalysis.value = false;
                // Find a plan for this sub-category. Use the current month's bar value as
                // currentSpend (budgets are monthly). Reset date is the end of the current
                // calendar month — the 6-month window's toDate would be misleading here.
                const matchingCat = cats.find((c) => c.plan && c.subCategory === subName);
                if (matchingCat) {
                    const idx = findCurrentMonthIndex(monthlyData.value);
                    const currentSpend = Math.abs((idx !== undefined ? monthlyData.value[idx]?.value : 0) ?? 0);
                    const now = new Date();
                    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                    categoryBudget.value = {
                        categoryName: matchingCat.subCategory,
                        currentSpend,
                        planAmount: matchingCat.plan.amount,
                        resetDate: monthEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }),
                    };
                }
                else {
                    categoryBudget.value = null;
                }
                for (const cat of cats) {
                    const merchants = cat?.spendByMerchant ?? [];
                    for (const m of merchants) {
                        if (!m?.merchantName || !merchantNames.has(m.merchantName))
                            continue;
                        for (const tx of (m.transactions ?? [])) {
                            if (tx.id === t.id && tx.switchable === true)
                                switchableFromAnalysis.value = true;
                            flat.push(reshapeSpendingTx(tx));
                        }
                    }
                }
                merchantTxGroups.value = groupTransactionsByDay(flat);
            }, /* refreshCache */ false, /* staleWhileRevalidate */ true, pollUntilChanged);
        }
        // ---------- Event handlers ----------
        // Passed to TransactionsList while a category update is in flight. Returning
        // null instead would trigger TransactionsList's legacy "empty modal" fallback,
        // so a no-op handler is needed to make rows genuinely non-interactive.
        const noop = () => { };
        // Clicking another transaction in either list navigates to that transaction's
        // detail page instead of the legacy iframe modal. A click on the current
        // transaction is a no-op.
        function onTransactionClick(t) {
            if (!t?.id || t.id === transactionId.value)
                return;
            transactionsDrawerOpen.value = false;
            router.push('/TransactionDetail/' + t.id);
        }
        function onDiscountClick() {
            const voucherId = merchantDiscount.value?.id;
            if (voucherId) {
                router.push('/vouchers/amount/' + voucherId);
                return;
            }
            router.push('/shopvouchers');
        }
        function viewAllForMerchant() {
            selectedFilter.value = 'all';
            selectedSubCategory.value = '';
            selectedSubCategoryColour.value = '';
            selectedMerchant.value = merchantName.value;
            cameFromTransactionDetail.value = true;
            router.push('/SpendingAnalysis');
        }
        function onDrawerTagChange(tags) {
            handleTagsChange(tags.map(tag => ({ tag })));
        }
        function onCategoryApply(payload) {
            const t = transaction.value;
            if (!payload.classification || !t?.id)
                return;
            // Tell other pages (e.g. MoneyPage) that the cached banking payload is
            // about to go stale so they SWR+poll on their next mount.
            pendingBankingRevalidate.value = true;
            const currentClassId = t.effectiveClassification?.id ?? null;
            const currentExclude = t.excludeFromAnalysis ?? false;
            const expectedClassId = payload.classification?.id ?? null;
            const expectedExclude = payload.excludeFromAnalysis ?? false;
            const classChanged = expectedClassId !== currentClassId;
            const excludeChanged = expectedExclude !== currentExclude;
            const excludeOnlyRule = excludeChanged && !classChanged && payload.alwaysApplyExcludeInclude;
            const expectsSelfChange = classChanged || (excludeChanged && !excludeOnlyRule);
            // Optimistically reflect the exclude flip in any cached banking transactions
            // so other pages (e.g. MoneyPage) show the excluded icon as soon as the user
            // navigates, without waiting for the banking poll. The poll can exit early
            // — the server hash flips for unrelated fields, or the exclude rule hasn't
            // propagated to the cached banking response yet — so relying on it alone
            // leaves the icon missing until a manual refresh.
            if (excludeChanged) {
                const ruleScoped = payload.alwaysApplyExcludeInclude;
                const txMerchantName = t.merchantName || merchantName.value;
                applyExcludeOptimistically((tx) => ruleScoped
                    ? (!!txMerchantName && (tx.merchantName === txMerchantName || tx.merchant?.name === txMerchantName))
                    : tx.id === t.id, expectedExclude);
            }
            const finishWithoutSelfChange = () => {
                pendingWriteRevalidate.value = true;
                if (excludeChanged && transaction.value) {
                    transaction.value = { ...transaction.value, excludeFromAnalysis: expectedExclude };
                }
                // Safety net: useApi silently swallows network errors when SWR is on, so
                // the !isStale callback may never fire on a failed banking request. The
                // expectsSelfChange path has pollForChange.onFinish as its fallback; this
                // path needs its own, otherwise the lozenge stays stuck in "Working...."
                // until a full page reload. 30s comfortably exceeds the banking poll's
                // 5×5s budget so it only kicks in for genuine failures.
                const clearTimer = setTimeout(() => {
                    if (inflightCategoryUpdate.value?.id === t.id) {
                        inflightCategoryUpdate.value = null;
                    }
                }, 30000);
                getBankingInfo((_result, isStale) => {
                    if (!isStale && inflightCategoryUpdate.value?.id === t.id) {
                        inflightCategoryUpdate.value = null;
                        clearTimeout(clearTimer);
                    }
                }, false, null, 
                /* staleWhileRevalidate */ true, 
                /* pollUntilChanged */ true);
            };
            // Capture the pre-write txn hash + a fields fallback; both the upfront
            // poll below and any on-remount repoll (loadTransaction) compare against
            // these. The fields fallback covers the case where the server response
            // carries no x-mgcache-md5 header (non-/Cached/ endpoints).
            const baselineHash = currentTransactionHash.value;
            const baselineFields = txnFieldsKey(t);
            const pollForChange = () => {
                pollTxnUntilHashDiffers(t.id, baselineHash, baselineFields, ({ latestFetched, hadChange }) => {
                    if (hadChange && latestFetched) {
                        transaction.value = latestFetched;
                        currentTransactionHash.value = transactionsApi.getCachedTransactionHash(t.id);
                    }
                    // The in-flight marker (which drives both lozenge + row spinners
                    // via their computeds) is normally cleared by the SA !isStale
                    // callback. Fall back to clearing it here only when the txn poll
                    // exhausted attempts without detecting a change — otherwise the
                    // UI would stay in the "Working...." state indefinitely.
                    if (!hadChange && inflightCategoryUpdate.value?.id === t.id) {
                        inflightCategoryUpdate.value = null;
                    }
                });
            };
            // Shared marker — drives the lozenge + row spinners via their computeds
            // and persists across navigation so a quick out-and-back into TDP restores
            // the in-flight UI. Set upfront (before applyCategoryToTransactions) so the
            // lozenge disables the instant Update is tapped, not after the server reply.
            // `allMerchant: true` only when the user opted into "always apply" AND it's
            // a real category change; an exclude-only-with-always-apply still scopes
            // the spinner to the tapped row (other rows aren't reclassified).
            inflightCategoryUpdate.value = {
                id: t.id,
                allMerchant: expectsSelfChange && !!payload.alwaysApply,
                baselineHash,
                baselineFields,
            };
            if (expectsSelfChange) {
                // Signal to AccountPage / MoneyPage / SpendingAnalysisPage that this
                // transaction has a pending change so they can show a row spinner on mount
                // while their SWR+poll waits for the server payload to reflect it.
                pendingTransactionUpdateId.value = t.id;
                // Kick off the SA + banking revalidations immediately, in parallel with
                // the transaction poll. Don't wait for the txn hash to flip: if the user
                // navigates away mid-poll, these caches still converge in the background
                // so the next view of TDP / SpendingAnalysisPage doesn't show stale data.
                pendingWriteRevalidate.value = true;
                getBankingInfo(null, false, null, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
                // Suppress the classification-key watcher's next firing — pollForChange
                // will reassign transaction.value on success, which flips that key, but
                // the SA fetch we kick off below already covers the same poll-until-
                // changed cycle. Without this gate the watcher fires a second redundant
                // fetchSummary, costing an extra SWR round-trip and re-render per apply.
                skipNextSummaryFetch.value = true;
                fetchSummary();
            }
            // Ensure the helper has a merchantName to dedupe / build a rule from,
            // even when the transaction itself doesn't carry one.
            const txForApply = { ...t, merchantName: t.merchantName || merchantName.value };
            transactionsApi.applyCategoryToTransactions([txForApply], payload, expectsSelfChange ? pollForChange : finishWithoutSelfChange);
        }
        function handleTagsChange(tags) {
            const t = transaction.value;
            if (!t?.id)
                return;
            const existing = (t.customerTags ?? []);
            const updatedTags = tags.map((newTag) => {
                const text = newTag.tag || newTag;
                const match = existing.find((ct) => ct.tag === text);
                return match ? { tag: match.tag, id: match.id } : { tag: text };
            });
            selectedTags.value = updatedTags.map(u => u.tag);
            transactionsApi.updateTransactionTags(t.bankAccountID, t.id, updatedTags, () => {
                // Bust the per-tag /Transactions caches so totals refetch fresh, but
                // KEEP the bare tag-list entry — pollUntilChanged below uses it as its
                // baseline to detect when the newly-created tag lands server-side.
                clearCustomerTagsCache(/* preserveList */ true);
                // Deliberately do NOT clear the banking / spending-analysis caches here.
                // useApi's pollUntilChanged needs an existing cached payload as the
                // baseline to compare each refetch against — wiping the cache disables
                // the poll, and the next fetch may hit the server's /Cached/ response
                // before the tag write has propagated, showing stale tags until the
                // next manual refresh. Setting the two handoff flags below tells SA /
                // MoneyPage to SWR+poll on their next mount until the payload changes.
                pendingBankingRevalidate.value = true;
                pendingWriteRevalidate.value = true;
                // Background-poll the customer-tags list until the server reflects the
                // new tag (the change key is the set of tag ids, so a freshly-created
                // tag flips it). Skip the stale emission — the cached list predates the
                // write — and rebuild availableTags only from the fresh payload.
                getCustomerTags((_result, isStale) => {
                    if (isStale)
                        return;
                    availableTags.value = (state.customerTags ?? []).map((ct) => ct.tag);
                }, /* refreshCache */ false, /* staleWhileRevalidate */ true, /* pollUntilChanged */ true);
                transactionsApi.getTransaction(t.id, (fresh) => {
                    if (fresh?.id === transactionId.value) {
                        transaction.value = fresh;
                        currentTransactionHash.value = transactionsApi.getCachedTransactionHash(t.id);
                    }
                });
            });
        }
        function onLogoError(event) {
            onMerchantLogoError(event, merchantLogoInput.value);
        }
        function openCategoryDrawer() {
            nextTick(() => {
                categoryDrawer.value?.openDrawer();
            });
        }
        function openTransactionsDrawer() {
            transactionsDrawerOpen.value = true;
        }
        // ---------- Lifecycle ----------
        onMounted(() => {
            // Defer past App.vue's `route.path` watcher which strips this class on every nav.
            nextTick(() => document.body.classList.add('hide-nav-bar'));
            // If we left TDP mid-flight and came back, the original SA poll is still
            // running but writes to the previous (now orphaned) instance's refs.
            // Re-arm pendingWriteRevalidate so this instance's fetchSummary also runs
            // with pollUntilChanged — its callback then updates *this* merchantTxGroups
            // in sync with the in-flight marker clearing.
            if (inflightCategoryUpdate.value?.id === transactionId.value) {
                pendingWriteRevalidate.value = true;
            }
            loadTransaction(transactionId.value);
            getCustomerTags(() => {
                availableTags.value = (state.customerTags ?? []).map((ct) => ct.tag);
            });
            if (!state.bankInfo)
                getBankingInfo();
            getAvailableVouchers();
        });
        onUnmounted(() => {
            document.body.classList.remove('hide-nav-bar');
        });
        // Re-load when navigating between transaction detail pages (route param change).
        watch(transactionId, loadTransaction);
        // When the transaction loads and has a retail-card discount, fetch the
        // recommended-voucher entry for this merchant so we can show spend / saving.
        watch(merchantDiscount, (d) => {
            if (!d) {
                merchantRecommendedVoucher.value = null;
                return;
            }
            const sysoMerchantId = d.merchant?.sysoMerchantId;
            if (!sysoMerchantId)
                return;
            getRecommendedVouchers(-365, (recs) => {
                merchantRecommendedVoucher.value = (recs ?? []).find((v) => v.merchant?.sysoMerchantId === sysoMerchantId) ?? null;
            });
        });
        // Refetch the chart only when the fields it actually depends on change —
        // otherwise unrelated updates (e.g. saving a hashtag) would redraw the chart.
        watch(() => {
            const t = transaction.value;
            if (!t)
                return '';
            return `${t.effectiveClassification?.mgSubClassification ?? ''}|${t.merchantName ?? ''}|${t.merchant?.name ?? ''}`;
        }, () => {
            if (skipNextSummaryFetch.value) {
                skipNextSummaryFetch.value = false;
                return;
            }
            fetchSummary();
        });
        return (_ctx, _cache) => {
            const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(HeaderBlock, {
                    title: "",
                    overlay: true
                }),
                _createElementVNode("div", _hoisted_1, [
                    (!transaction.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                            _createVNode(_component_v_skeleton_loader, {
                                type: "avatar",
                                class: "skeleton-avatar"
                            }),
                            _createVNode(_component_v_skeleton_loader, {
                                type: "heading",
                                class: "skeleton-heading"
                            })
                        ]))
                        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _createElementVNode("div", {
                                class: "merchant-avatar",
                                style: _normalizeStyle({ backgroundColor: merchantLogoStyle.value.backgroundColor })
                            }, [
                                _createElementVNode("img", {
                                    class: _normalizeClass(avatarIsCategory.value ? 'merchant-avatar-icon' : 'merchant-avatar-logo'),
                                    src: merchantLogoUrl.value,
                                    onError: onLogoError,
                                    alt: ""
                                }, null, 42, _hoisted_4)
                            ], 4),
                            _createElementVNode("h2", _hoisted_5, _toDisplayString(merchantName.value), 1),
                            _createElementVNode("div", {
                                class: _normalizeClass(["transaction-amount", { 'amount-positive': isIncome.value }])
                            }, _toDisplayString(amountDisplay.value), 3)
                        ])),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 2,
                            type: "chip",
                            class: "skeleton-lozenge"
                        }))
                        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            (merchantName.value)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    class: "view-merchant-link",
                                    onClick: viewAllForMerchant
                                }, [
                                    _createElementVNode("span", null, "View all " + _toDisplayString(merchantName.value), 1),
                                    _createElementVNode("img", {
                                        class: "view-merchant-arrow",
                                        src: require('@/assets/arrow-right.png'),
                                        alt: ""
                                    }, null, 8, _hoisted_7)
                                ]))
                                : _createCommentVNode("", true),
                            _createVNode(CategoryLozenge, {
                                categoryName: categoryName.value,
                                iconPath: transaction.value?.effectiveClassification?.iconPath,
                                iconColour: transaction.value?.effectiveClassification?.color,
                                disabled: categoryUpdating.value,
                                disabledLabel: "Working....",
                                onClick: openCategoryDrawer
                            }, null, 8, ["categoryName", "iconPath", "iconColour", "disabled"]),
                            (!categoryUpdating.value)
                                ? (_openBlock(), _createElementBlock("p", {
                                    key: 1,
                                    class: "category-hint",
                                    onClick: openCategoryDrawer
                                }, "Not right? Change your category"))
                                : _createCommentVNode("", true)
                        ])),
                    (merchantDiscount.value)
                        ? (_openBlock(), _createBlock(RetailDiscountCard, {
                            key: 4,
                            voucher: merchantDiscount.value,
                            onClick: onDiscountClick
                        }, {
                            default: _withCtx(() => [
                                (discountSpendDisplay.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _cache[2] || (_cache[2] = _createTextVNode(" You’ve spent ")),
                                        _createElementVNode("strong", null, _toDisplayString(discountSpendDisplay.value), 1),
                                        _cache[3] || (_cache[3] = _createTextVNode(" in the last 12 months. You could have saved up to ")),
                                        _createElementVNode("strong", null, _toDisplayString(discountSavingDisplay.value), 1),
                                        _cache[4] || (_cache[4] = _createTextVNode(" with Retail Cards. "))
                                    ], 64))
                                    : _createCommentVNode("", true)
                            ]),
                            _: 1
                        }, 8, ["voucher"]))
                        : _createCommentVNode("", true),
                    (showSwitchToSave.value)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 5,
                            class: "switch-card",
                            href: switchToSaveHref.value
                        }, [
                            _createElementVNode("div", _hoisted_9, [
                                _createElementVNode("div", _hoisted_10, [
                                    _createElementVNode("div", _hoisted_11, [
                                        _createElementVNode("img", {
                                            class: "switch-avatars-img",
                                            src: require('@/assets/SwitchAvatars.png'),
                                            alt: ""
                                        }, null, 8, _hoisted_12)
                                    ]),
                                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "switch-title" }, "Switch to save", -1))
                                ]),
                                _createElementVNode("img", {
                                    class: "switch-arrow",
                                    src: require('@/assets/arrow-right.png'),
                                    alt: ""
                                }, null, 8, _hoisted_13)
                            ]),
                            _cache[6] || (_cache[6] = _createElementVNode("p", { class: "switch-desc" }, "Compare deals available to you with our trusted partner, ApTap.", -1))
                        ], 8, _hoisted_8))
                        : _createCommentVNode("", true),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 6,
                            type: "image",
                            class: "skeleton-details"
                        }))
                        : (_openBlock(), _createBlock(WellPanel, {
                            key: 7,
                            data: detailsWellData.value,
                            class: "details-well"
                        }, null, 8, ["data"])),
                    (!transaction.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 8,
                            type: "list-item-avatar-two-line, list-item-avatar-two-line",
                            class: "skeleton-tx"
                        }))
                        : (merchantTxGroups.value.length > 0)
                            ? (_openBlock(), _createBlock(RecentTransactionsSection, {
                                key: 9,
                                title: 'Transactions - ' + merchantName.value,
                                transactions: merchantTxGroups.value,
                                formatter: _unref(state).formatter,
                                today: _unref(state).today,
                                yesterday: _unref(state).yesterday,
                                limit: 4,
                                onTransactionClick: categoryUpdating.value ? noop : onTransactionClick,
                                categoryPills: true,
                                updatingTransactionId: updatingTransactionId.value,
                                onViewMore: openTransactionsDrawer
                            }, null, 8, ["title", "transactions", "formatter", "today", "yesterday", "onTransactionClick", "updatingTransactionId"]))
                            : _createCommentVNode("", true),
                    (!transaction.value || summaryLoading.value)
                        ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                            key: 10,
                            type: "image",
                            class: "skeleton-summary"
                        }))
                        : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                            _createElementVNode("h3", _hoisted_15, _toDisplayString(merchantName.value) + " trend", 1),
                            (_openBlock(), _createBlock(SpendingSummaryCard, {
                                key: 'td-summary-' + merchantName.value,
                                mode: "category",
                                categoryLabel: merchantName.value,
                                monthlyData: monthlyData.value,
                                currentMonthIndex: currentMonthIndex.value,
                                barColour: barColour.value
                            }, _createSlots({ _: 2 }, [
                                (categoryBudget.value)
                                    ? {
                                        name: "footer",
                                        fn: _withCtx(() => [
                                            _createVNode(BudgetSummaryCard, {
                                                categoryName: categoryBudget.value.categoryName,
                                                currentSpend: categoryBudget.value.currentSpend,
                                                planAmount: categoryBudget.value.planAmount,
                                                resetDate: categoryBudget.value.resetDate,
                                                inline: "",
                                                onClick: _cache[0] || (_cache[0] = ($event) => (_unref(router).push('/SpendingPlan')))
                                            }, null, 8, ["categoryName", "currentSpend", "planAmount", "resetDate"])
                                        ]),
                                        key: "0"
                                    }
                                    : undefined
                            ]), 1032, ["categoryLabel", "monthlyData", "currentMonthIndex", "barColour"]))
                        ])),
                    _createVNode(AiChatBanner, { class: "aiChat" }),
                    (transaction.value && availableTags.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                            _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "tagHeading" }, "Add a transaction tag", -1)),
                            _createVNode(TagInput, {
                                selectableTags: availableTags.value,
                                modelValue: selectedTags.value,
                                maxTags: 5,
                                maxTagLength: 20,
                                placeholder: "Tag limit reached — remove one to add another",
                                chipsBelow: true,
                                onChange: handleTagsChange,
                                class: "tagInputBox"
                            }, null, 8, ["selectableTags", "modelValue"]),
                            _cache[8] || (_cache[8] = _createElementVNode("p", { class: "tagHelp" }, "Find your transaction tags on the Activity page. Try using them to group transactions from a holiday, or project.", -1))
                        ]))
                        : _createCommentVNode("", true)
                ]),
                _createVNode(SimpleDrawerPanel, {
                    modelValue: transactionsDrawerOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((transactionsDrawerOpen).value = $event)),
                    title: 'Transactions - ' + merchantName.value,
                    height: "90vh"
                }, {
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                            (allMerchantTransactions.value.length > 0)
                                ? (_openBlock(), _createBlock(TransactionsList, {
                                    key: 0,
                                    search: true,
                                    showDateTotal: false,
                                    transactions: allMerchantTransactions.value,
                                    formatter: _unref(state).formatter,
                                    today: _unref(state).today,
                                    yesterday: _unref(state).yesterday,
                                    dividingLines: true,
                                    categoryPills: true,
                                    updatingTransactionId: updatingTransactionId.value,
                                    onTransactionClick: categoryUpdating.value ? noop : onTransactionClick
                                }, null, 8, ["transactions", "formatter", "today", "yesterday", "updatingTransactionId", "onTransactionClick"]))
                                : _createCommentVNode("", true)
                        ])
                    ]),
                    _: 1
                }, 8, ["modelValue", "title"]),
                _createVNode(ChangeCategoryDrawer, {
                    ref_key: "categoryDrawer",
                    ref: categoryDrawer,
                    merchantName: merchantName.value,
                    currentClassification: transaction.value?.effectiveClassification,
                    currentExcludeFromAnalysis: transaction.value?.excludeFromAnalysis ?? false,
                    selectableTags: availableTags.value ?? [],
                    currentTags: selectedTags.value,
                    onApply: onCategoryApply,
                    onTagChange: onDrawerTagChange
                }, null, 8, ["merchantName", "currentClassification", "currentExcludeFromAnalysis", "selectableTags", "currentTags"])
            ], 64));
        };
    }
});
